import React, { useState, useEffect, useCallback } from 'react';
import { 
  Form, 
  Card, 
  Radio, 
  InputNumber, 
  Select, 
  DatePicker, 
  Checkbox, 
  Button, 
  Space, 
  Alert, 
  Timeline,
  Divider,
  Row,
  Col,
  Typography
} from 'antd';
import { CalendarOutlined, ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import type { 
  CycleConfig, 
  CycleFrequencyType, 
  TerminationType, 
  ValidationError 
} from '../../../../types/plan-template';
import { 
  generateFirstExecutionTimestamp, 
  timestampToDateTime, 
  dateStringToTimestamp,
  getCurrentTimestamp 
} from '../../../../utils/dateTimeUtil';

const { Option } = Select;
const { Text, Title } = Typography;

interface CycleConfigurationProps {
  templateData: any;
  onDataChange: (data: any) => void;
  onValidationChange: (isValid: boolean, errors?: ValidationError[]) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const CycleConfiguration: React.FC<CycleConfigurationProps> = ({
  templateData,
  onDataChange,
  onValidationChange,
  onNext,
  onPrevious
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  
  // Weekday options for weekly cycles
  const WEEKDAY_OPTIONS = [
    { label: t('planTemplate.wizard.cycleConfiguration.weekdays.monday'), value: 1 },
    { label: t('planTemplate.wizard.cycleConfiguration.weekdays.tuesday'), value: 2 },
    { label: t('planTemplate.wizard.cycleConfiguration.weekdays.wednesday'), value: 3 },
    { label: t('planTemplate.wizard.cycleConfiguration.weekdays.thursday'), value: 4 },
    { label: t('planTemplate.wizard.cycleConfiguration.weekdays.friday'), value: 5 },
    { label: t('planTemplate.wizard.cycleConfiguration.weekdays.saturday'), value: 6 },
    { label: t('planTemplate.wizard.cycleConfiguration.weekdays.sunday'), value: 0 }
  ];

  // Month day options for monthly cycles
  const MONTH_DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
    label: `${i + 1}${t('planTemplate.wizard.cycleConfiguration.monthDays.day')}`,
    value: i + 1
  }));

  const [cycleConfig, setCycleConfig] = useState<CycleConfig>(() => {
    // Initialize with default values or existing data
    return templateData.executionCycle || {
      frequencyType: 'Weekly',
      interval: 1,
      weekDaySelection: [1], // Default to Monday
      monthDaySelection: [1], // Default to 1st day
      firstExecutionDate: generateFirstExecutionTimestamp(dayjs().add(1, 'day').format('YYYY-MM-DD')),
      terminationType: 'Infinite Repeat',
      cycleStatus: 'Active'
    };
  });

  const [previewDates, setPreviewDates] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // Update form when cycleConfig changes
  useEffect(() => {
    form.setFieldsValue({
      frequencyType: cycleConfig.frequencyType,
      interval: cycleConfig.interval,
      weekDaySelection: cycleConfig.weekDaySelection,
      monthDaySelection: cycleConfig.monthDaySelection,
      firstExecutionDate: cycleConfig.firstExecutionDate ? dayjs(cycleConfig.firstExecutionDate) : null,
      terminationType: cycleConfig.terminationType,
      endDate: cycleConfig.endDate ? dayjs(cycleConfig.endDate) : null,
      repeatCount: cycleConfig.repeatCount
    });
  }, [cycleConfig, form]);

  // Generate preview dates when configuration changes
  useEffect(() => {
    generatePreviewDates();
  }, [cycleConfig]);

  // Validate configuration and update parent
  useEffect(() => {
    const errors = validateCycleConfig();
    setValidationErrors(errors);
    const isValid = errors.length === 0;
    onValidationChange(isValid, errors);
    
    if (isValid) {
      onDataChange({ executionCycle: cycleConfig });
    }
  }, [cycleConfig, onValidationChange, onDataChange]);

  const validateCycleConfig = (): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Validate frequency type and interval
    if (!cycleConfig.frequencyType) {
      errors.push({ field: 'frequencyType', message: t('planTemplate.wizard.cycleConfiguration.validation.selectFrequencyType'), type: 'error' });
    }

    if (!cycleConfig.interval || cycleConfig.interval < 1) {
      errors.push({ field: 'interval', message: t('planTemplate.wizard.cycleConfiguration.validation.intervalMustBePositive'), type: 'error' });
    }

    // Validate first execution date
    if (!cycleConfig.firstExecutionDate) {
      errors.push({ field: 'firstExecutionDate', message: t('planTemplate.wizard.cycleConfiguration.validation.selectFirstExecutionDate'), type: 'error' });
    } else {
      const firstDate = dayjs(cycleConfig.firstExecutionDate);
      if (firstDate.isBefore(dayjs(), 'day')) {
        errors.push({ field: 'firstExecutionDate', message: t('planTemplate.wizard.cycleConfiguration.validation.firstDateCannotBePast'), type: 'error' });
      }
    }

    // Validate weekday selection for weekly cycles
    if (cycleConfig.frequencyType === 'Weekly') {
      if (!cycleConfig.weekDaySelection || cycleConfig.weekDaySelection.length === 0) {
        errors.push({ field: 'weekDaySelection', message: t('planTemplate.wizard.cycleConfiguration.validation.selectWeekdays'), type: 'error' });
      }
    }

    // Validate month day selection for monthly cycles
    if (cycleConfig.frequencyType === 'Monthly') {
      if (!cycleConfig.monthDaySelection || cycleConfig.monthDaySelection.length === 0) {
        errors.push({ field: 'monthDaySelection', message: t('planTemplate.wizard.cycleConfiguration.validation.selectMonthDays'), type: 'error' });
      }
    }

    // Validate termination settings
    if (cycleConfig.terminationType === 'End on Date') {
      if (!cycleConfig.endDate) {
        errors.push({ field: 'endDate', message: t('planTemplate.wizard.cycleConfiguration.validation.selectEndDate'), type: 'error' });
      } else {
        const endDate = dayjs(cycleConfig.endDate);
        const firstDate = dayjs(cycleConfig.firstExecutionDate);
        if (endDate.isBefore(firstDate)) {
          errors.push({ field: 'endDate', message: t('planTemplate.wizard.cycleConfiguration.validation.endDateCannotBeBeforeFirst'), type: 'error' });
        }
      }
    }

    if (cycleConfig.terminationType === 'Limited Count') {
      if (!cycleConfig.repeatCount || cycleConfig.repeatCount < 1) {
        errors.push({ field: 'repeatCount', message: t('planTemplate.wizard.cycleConfiguration.validation.repeatCountMustBePositive'), type: 'error' });
      }
      if (cycleConfig.repeatCount && cycleConfig.repeatCount > 1000) {
        errors.push({ field: 'repeatCount', message: t('planTemplate.wizard.cycleConfiguration.validation.repeatCountMaxLimit'), type: 'error' });
      }
    }

    return errors;
  };

  const generatePreviewDates = useCallback(() => {
    switch (cycleConfig.frequencyType) {
      case 'Daily':
        calPreviewByDay();
        break;
        
      case 'Weekly':
        calPreviewByWeek();
        break;
        
      case 'Monthly':
        calPreviewByMonth();
        break;
        
      case 'Yearly':
        calPreviewByYear();
        break;
        
      default:
        break;
    }

    // if (!cycleConfig.firstExecutionDate || !cycleConfig.frequencyType) {
    //   setPreviewDates([]);
    //   return;
    // }

    // const dates: string[] = [];
    // const firstExecutionDate = dayjs(cycleConfig.firstExecutionDate);
    // let currentDate = firstExecutionDate.clone();
    // const maxPreviewCount = 10; // Show up to 10 upcoming dates
    // let count = 0;

    // // Determine end condition
    // const hasEndDate = cycleConfig.terminationType === 'End on Date' && cycleConfig.endDate;
    // const hasRepeatLimit = cycleConfig.terminationType === 'Limited Count' && cycleConfig.repeatCount;
    // const endDate = hasEndDate ? dayjs(cycleConfig.endDate) : null;
    // const maxCount = hasRepeatLimit ? Math.min(cycleConfig.repeatCount!, maxPreviewCount) : maxPreviewCount;

    // // 根据频率类型计算第一个符合规律的执行日期（在首次执行日期之后）
    // switch (cycleConfig.frequencyType) {
    //   case 'Daily':
    //     // 对于每日执行，第一个执行日期就是首次执行日期加上间隔天数
    //     currentDate = currentDate.add(cycleConfig.interval, 'day');
    //     break;
        
    //   case 'Weekly':
    //     // 对于每周执行，需要找到首次执行日期之后符合星期选择的第一个日期
    //     if (cycleConfig.weekDaySelection && cycleConfig.weekDaySelection.length > 0) {
    //       // 先将日期加一天，确保我们查找的是首次执行日期之后的日期
    //       // currentDate = currentDate.add(1, 'day');
          
    //       // 寻找第一个匹配的星期几
    //       while (!cycleConfig.weekDaySelection.includes(currentDate.day())) {
    //         currentDate = currentDate.add(1, 'day');
    //       }
    //     } else {
    //       // 如果没有选择特定的星期几，默认按照间隔周计算
    //       currentDate = currentDate.add(cycleConfig.interval, 'week');
    //     }
    //     break;
        
    //   case 'Monthly':
        // // 对于每月执行，需要找到首次执行日期之后符合月份日期选择的第一个日期
        // currentDate = currentDate.add(cycleConfig.interval, 'month');
        // if (cycleConfig.monthDaySelection && cycleConfig.monthDaySelection.length > 0) {
        //   // 调整到符合月份日期选择的第一个日期
        //   // 查找该月第一个匹配的日期
        //   let found = false;
        //   // 重置到该月的第一天
        //   currentDate = currentDate.date(1);
        //   // 遍历查找该月的第一个匹配日期
        //   for (let i = 0; i < 31; i++) {
        //     if (cycleConfig.monthDaySelection.includes(currentDate.date())) {
        //       found = true;
        //       break;
        //     }
        //     // 如果已经是该月的最后一天，则跳出循环
        //     if (currentDate.date() >= 28 && currentDate.add(1, 'day').month() !== currentDate.month()) {
        //       break;
        //     }
        //     currentDate = currentDate.add(1, 'day');
        //   }
          
        //   // 如果该月没有匹配的日期，则找下个月的第一个匹配日期
        //   if (!found) {
        //     // 移动到下个月的第一天
        //     currentDate = currentDate.add(1, 'month').date(1);
        //     // 查找下个月的第一个匹配日期
        //     const firstMatchingDay = cycleConfig.monthDaySelection.find(day => day >= 1) || 1;
        //     currentDate = currentDate.date(firstMatchingDay);
        //   }
        // }
    //     break;
        
    //   case 'Yearly':
    //     // 对于年度执行，第一个执行日期就是首次执行日期加上间隔年数
    //     currentDate = currentDate.add(cycleConfig.interval, 'year');
    //     break;
        
    //   default:
    //     break;
    // }

    // // 根据频率类型计算后续的执行日期
    // while (count < maxCount) {
    //   // Check if we've reached the end date
    //   if (endDate && currentDate.isAfter(endDate)) {
    //     break;
    //   }

    //   // Add current date to preview
    //   dates.push(currentDate.format('YYYY-MM-DD'));
    //   count++;

    //   // Calculate next date based on frequency type
    //   switch (cycleConfig.frequencyType) {
    //     case 'Daily':
    //       currentDate = currentDate.add(cycleConfig.interval, 'day');
    //       break;

    //     case 'Weekly':
    //       if (cycleConfig.weekDaySelection && cycleConfig.weekDaySelection.length > 0) {
    //         // For weekly cycles with specific weekdays
    //         let nextDate = currentDate.add(1, 'day');
    //         let found = false;
            
    //         // Look for the next occurrence within the current week
    //         for (let i = 0; i < 7; i++) {
    //           if (cycleConfig.weekDaySelection.includes(nextDate.day())) {
    //             currentDate = nextDate;
    //             found = true;
    //             break;
    //           }
    //           nextDate = nextDate.add(1, 'day');
    //         }
            
    //         // If no more occurrences in current week, move to next interval
    //         if (!found) {
    //           // Move to the start of next interval
    //           currentDate = currentDate.add(cycleConfig.interval - 1, 'week');
    //           // Find first matching weekday in the new week
    //           for (let i = 0; i < 7; i++) {
    //             if (cycleConfig.weekDaySelection.includes(currentDate.day())) {
    //               break;
    //             }
    //             currentDate = currentDate.add(1, 'day');
    //           }
    //         }
    //       } else {
    //         currentDate = currentDate.add(cycleConfig.interval, 'week');
    //       }
    //       break;

    //     case 'Monthly':
          // if (cycleConfig.monthDaySelection && cycleConfig.monthDaySelection.length > 0) {
          //   // For monthly cycles with specific days
          //   let nextDate = currentDate.add(1, 'day');
          //   let found = false;
            
          //   // Look for the next occurrence within the current month
          //   while (nextDate.month() === currentDate.month()) {
          //     if (cycleConfig.monthDaySelection.includes(nextDate.date())) {
          //       currentDate = nextDate;
          //       found = true;
          //       break;
          //     }
          //     nextDate = nextDate.add(1, 'day');
          //   }
            
          //   // If no more occurrences in current month, move to next interval
          //   if (!found) {
          //     currentDate = currentDate.add(cycleConfig.interval, 'month');
          //     // Find first matching day in the new month
          //     const firstMatchingDay = Math.min(...cycleConfig.monthDaySelection);
          //     currentDate = currentDate.date(firstMatchingDay);
          //   }
          // } else {
          //   currentDate = currentDate.add(cycleConfig.interval, 'month');
          // }
    //       break;

    //     case 'Yearly':
    //       currentDate = currentDate.add(cycleConfig.interval, 'year');
    //       break;

    //     default:
    //       break;
    //   }
    // }

    // setPreviewDates(dates);
  }, [cycleConfig]);

  const calPreviewByDay = () => {
    if (!cycleConfig.firstExecutionDate || !cycleConfig.frequencyType) {
      setPreviewDates([]);
      return;
    }
    const dates: string[] = [];
    const firstExecutionDate = dayjs(cycleConfig.firstExecutionDate);
    let currentDate = firstExecutionDate.clone();
    // 如果 currentDate 在今天之前，则赋值为今天
    const today = dayjs().startOf('day');
    if (currentDate.isBefore(today)) {
      currentDate = today.clone();
    }
    const maxPreviewCount = 10; // Show up to 10 upcoming dates
    let count = 0;

    // Determine end condition
    const hasEndDate = cycleConfig.terminationType === 'End on Date' && cycleConfig.endDate;
    const hasRepeatLimit = cycleConfig.terminationType === 'Limited Count' && cycleConfig.repeatCount;
    const endDate = hasEndDate ? dayjs(cycleConfig.endDate) : null;
    const maxCount = hasRepeatLimit ? Math.min(cycleConfig.repeatCount!, maxPreviewCount) : maxPreviewCount;

    // currentDate = currentDate.add(cycleConfig.interval, 'day');
    
    while (count < maxCount) {
      // Check if we've reached the end date
      if (endDate && currentDate.isAfter(endDate)) {
        break;
      }

      // Add current date to preview
      dates.push(currentDate.format('YYYY-MM-DD'));
      count++;

      // Calculate next date based on frequency type
      currentDate = currentDate.add(cycleConfig.interval, 'day');
    }

    setPreviewDates(dates);
  }

  const calPreviewByWeek = () => {
    if (!cycleConfig.firstExecutionDate || !cycleConfig.frequencyType) {
      setPreviewDates([]);
      return;
    }

    const dates: string[] = [];
    const firstExecutionDate = dayjs(cycleConfig.firstExecutionDate);
    let currentDate = firstExecutionDate.clone();
    const maxPreviewCount = 10; // Show up to 10 upcoming dates
    let count = 0;

    // Determine end condition
    const hasEndDate = cycleConfig.terminationType === 'End on Date' && cycleConfig.endDate;
    const hasRepeatLimit = cycleConfig.terminationType === 'Limited Count' && cycleConfig.repeatCount;
    const endDate = hasEndDate ? dayjs(cycleConfig.endDate) : null;
    const maxCount = hasRepeatLimit ? Math.min(cycleConfig.repeatCount!, maxPreviewCount) : maxPreviewCount;

    currentDate = currentDate.day(1);
    const dayofWeeks = [];

    for (let i = 0; i < 7; i++) {
      if (cycleConfig.weekDaySelection && cycleConfig.weekDaySelection.length > 0) {
      // 先将日期加一天，确保我们查找的是首次执行日期之后的日期
      // currentDate = currentDate.add(1, 'day');
      
      // 寻找第一个匹配的星期几
        if (cycleConfig.weekDaySelection.includes(currentDate.day())) {
          dayofWeeks.push(currentDate);
        }
        currentDate = currentDate.add(1, 'day');
      }
    }

    let today = dayjs().startOf('day');
    // 如果 firstExecutionDate 在今天之前，则使用今天作为开始日期进行查询
    if (firstExecutionDate.isBefore(today)) {
      // 重新设置 currentDate 和 dayofWeeks，从今天开始查找符合条件的日期
      currentDate = today.clone();
      dayofWeeks.length = 0;
      for (let i = 0; i < 7; i++) {
        if (cycleConfig.weekDaySelection && cycleConfig.weekDaySelection.length > 0) {
          if (cycleConfig.weekDaySelection.includes(currentDate.day())) {
            dayofWeeks.push(currentDate);
          }
          currentDate = currentDate.add(1, 'day');
        }
      }
    }
    let currentCount = 0;
    for(let i = 0; i < 7; i++) {
      for(let j = 0; j < dayofWeeks.length; j++) {
        let date = dayofWeeks[j].add(i * cycleConfig.interval, 'week');
        if (endDate && date.isAfter(endDate)) {
          break;
        }
        if (date.date() == today.date() || date.isAfter(today)) {
          dates.push(date.format('YYYY-MM-DD'));
          currentCount++;
        }
        if (currentCount >= maxCount) break;
      }
      if (currentCount >= maxCount) break;
    }

    setPreviewDates(dates);
  }

  const calPreviewByMonth = () => {
    if (!cycleConfig.firstExecutionDate || !cycleConfig.frequencyType) {
      setPreviewDates([]);
      return;
    }

    const dates: string[] = [];
    const firstExecutionDate = dayjs(cycleConfig.firstExecutionDate);
    let currentDate = firstExecutionDate.clone();
    // 如果 currentDate 在今天之前，则赋值为今天
    const today = dayjs().startOf('day');
    if (currentDate.isBefore(today)) {
      currentDate = today.clone();
    }
    const maxPreviewCount = 10; // Show up to 10 upcoming dates
    let count = 0;

    // Determine end condition
    const hasEndDate = cycleConfig.terminationType === 'End on Date' && cycleConfig.endDate;
    const hasRepeatLimit = cycleConfig.terminationType === 'Limited Count' && cycleConfig.repeatCount;
    const endDate = hasEndDate ? dayjs(cycleConfig.endDate) : null;
    const maxCount = hasRepeatLimit ? Math.min(cycleConfig.repeatCount!, maxPreviewCount) : maxPreviewCount;
    
    // currentDate = currentDate.add(cycleConfig.interval, 'month');
    if (cycleConfig.monthDaySelection && cycleConfig.monthDaySelection.length > 0) {
      // 调整到符合月份日期选择的第一个日期
      // 查找该月第一个匹配的日期
      let found = false;
      // 重置到该月的第一天
      // currentDate = currentDate.date(1);
      // 遍历查找该月的第一个匹配日期
      for (let i = currentDate.date(); i < 31; i++) {
        if (cycleConfig.monthDaySelection.includes(currentDate.date())) {
          found = true;
          break;
        }
        // 如果已经是该月的最后一天，则跳出循环
        if (currentDate.date() >= 31 && currentDate.add(1, 'day').month() !== currentDate.month()) {
          break;
        }
        currentDate = currentDate.add(1, 'day');
      }
      
      // 如果该月没有匹配的日期，则找下个月的第一个匹配日期
      if (!found) {
        // 移动到下个月的第一天 已经加过了
        // currentDate = currentDate.add(1, 'month').date(1);
        // 查找下个月的第一个匹配日期
        const firstMatchingDay = cycleConfig.monthDaySelection.find(day => day >= 1) || 1;
        currentDate = currentDate.date(firstMatchingDay);
      }
    }

    while (count < maxCount) {
      // Check if we've reached the end date
      if (endDate && currentDate.isAfter(endDate)) {
        break;
      }

      // Add current date to preview
      dates.push(currentDate.format('YYYY-MM-DD'));
      count++;

      if (cycleConfig.monthDaySelection && cycleConfig.monthDaySelection.length > 0) {
        // For monthly cycles with specific days
        let nextDate = currentDate.add(1, 'day');
        let found = false;
        
        // Look for the next occurrence within the current month
        while (nextDate.month() === currentDate.month()) {
          if (cycleConfig.monthDaySelection.includes(nextDate.date())) {
            currentDate = nextDate;
            found = true;
            break;
          }
          nextDate = nextDate.add(1, 'day');
        }
        
        // If no more occurrences in current month, move to next interval
        if (!found) {
          currentDate = currentDate.add(cycleConfig.interval, 'month');
          // Find first matching day in the new month
          const firstMatchingDay = Math.min(...cycleConfig.monthDaySelection);
          currentDate = currentDate.date(firstMatchingDay);
        }
      } else {
        currentDate = currentDate.add(cycleConfig.interval, 'month');
      }
    }
    setPreviewDates(dates);
  }

  const calPreviewByYear = () => {
    if (!cycleConfig.firstExecutionDate || !cycleConfig.frequencyType) {
      setPreviewDates([]);
      return;
    }

    const dates: string[] = [];
    const firstExecutionDate = dayjs(cycleConfig.firstExecutionDate);
    let currentDate = firstExecutionDate.clone();
    // 如果 currentDate 在今天之前，则赋值为今天
    const today = dayjs().startOf('day');
    if (currentDate.isBefore(today)) {
      currentDate = today.clone();
    }
    const maxPreviewCount = 10; // Show up to 10 upcoming dates
    let count = 0;

    // Determine end condition
    const hasEndDate = cycleConfig.terminationType === 'End on Date' && cycleConfig.endDate;
    const hasRepeatLimit = cycleConfig.terminationType === 'Limited Count' && cycleConfig.repeatCount;
    const endDate = hasEndDate ? dayjs(cycleConfig.endDate) : null;
    const maxCount = hasRepeatLimit ? Math.min(cycleConfig.repeatCount!, maxPreviewCount) : maxPreviewCount;
    currentDate = currentDate.add(cycleConfig.interval, 'year');

    while (count < maxCount) {
      // Check if we've reached the end date
      if (endDate && currentDate.isAfter(endDate)) {
        break;
      }

      // Add current date to preview
      dates.push(currentDate.format('YYYY-MM-DD'));
      count++;

      // Check if we've reached the end date
      currentDate = currentDate.add(cycleConfig.interval, 'year');
    }
    setPreviewDates(dates);
  }

  const handleFrequencyTypeChange = (frequencyType: CycleFrequencyType) => {
    setCycleConfig(prev => ({
      ...prev,
      frequencyType,
      // Reset specific selections when changing frequency type
      weekDaySelection: frequencyType === 'Weekly' ? [1] : undefined,
      monthDaySelection: frequencyType === 'Monthly' ? [1] : undefined
    }));
  };

  const handleIntervalChange = (interval: number | null) => {
    if (interval && interval > 0) {
      setCycleConfig(prev => ({ ...prev, interval }));
    }
  };

  const handleWeekDaySelectionChange = (weekDaySelection: number[]) => {
    setCycleConfig(prev => ({ ...prev, weekDaySelection }));
  };

  const handleMonthDaySelectionChange = (monthDaySelection: number[]) => {
    setCycleConfig(prev => ({ ...prev, monthDaySelection }));
  };

  const handleFirstExecutionDateChange = (date: Dayjs | null) => {
    if (date) {
      // 设置时间为上午9点
      const dateTimeWithDefaultTime = date.hour(16).minute(0).second(0).millisecond(0);
      setCycleConfig(prev => ({
        ...prev,
        firstExecutionDate: dateTimeWithDefaultTime.valueOf()
      }));
    } else {
      setCycleConfig(prev => ({
        ...prev,
        firstExecutionDate: getCurrentTimestamp()
      }));
    }
  };

  const handleTerminationTypeChange = (terminationType: TerminationType) => {
    setCycleConfig(prev => ({
      ...prev,
      terminationType,
      // Clear related fields when changing termination type
      endDate: terminationType === 'End on Date' ? prev.endDate : undefined,
      repeatCount: terminationType === 'Limited Count' ? prev.repeatCount : undefined
    }));
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setCycleConfig(prev => ({
      ...prev,
      endDate: date ? date.valueOf() : undefined
    }));
  };

  const handleRepeatCountChange = (repeatCount: number | null) => {
    setCycleConfig(prev => ({
      ...prev,
      repeatCount: repeatCount || undefined
    }));
  };

  const renderFrequencySpecificOptions = () => {
    switch (cycleConfig.frequencyType) {
      case 'Weekly':
        return (
          <Form.Item
            label={t('planTemplate.wizard.cycleConfiguration.weekdays.selectWeekdays')}
            name="weekDaySelection"
            rules={[{ required: true, message: t('planTemplate.wizard.cycleConfiguration.validation.selectWeekdays') }]}
          >
            <Checkbox.Group
              options={WEEKDAY_OPTIONS}
              value={cycleConfig.weekDaySelection}
              onChange={handleWeekDaySelectionChange}
            />
          </Form.Item>
        );

      case 'Monthly':
        return (
          <Form.Item
            label={t('planTemplate.wizard.cycleConfiguration.monthDays.selectDays')}
            name="monthDaySelection"
            rules={[{ required: true, message: t('planTemplate.wizard.cycleConfiguration.validation.selectMonthDays') }]}
          >
            <Select
              mode="multiple"
              placeholder={t('planTemplate.wizard.cycleConfiguration.monthDays.placeholder')}
              value={cycleConfig.monthDaySelection}
              onChange={handleMonthDaySelectionChange}
              style={{ width: '100%' }}
            >
              {MONTH_DAY_OPTIONS.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );

      default:
        return null;
    }
  };

  const renderTerminationOptions = () => {
    switch (cycleConfig.terminationType) {
      case 'End on Date':
        return (
          <Form.Item
            label={t('planTemplate.wizard.cycleConfiguration.endDate')}
            name="endDate"
            rules={[{ required: true, message: t('planTemplate.wizard.cycleConfiguration.validation.selectEndDate') }]}
          >
            <DatePicker
              placeholder={t('common.selectEndTimePlaceHolder')}
              value={cycleConfig.endDate ? dayjs(cycleConfig.endDate) : null}
              onChange={handleEndDateChange}
              disabledDate={(current) => 
                current && current.isBefore(dayjs(cycleConfig.firstExecutionDate), 'day')
              }
              style={{ width: '100%' }}
            />
          </Form.Item>
        );

      case 'Limited Count':
        return (
          <Form.Item
            label={t('planTemplate.wizard.cycleConfiguration.repeatCount')}
            name="repeatCount"
            rules={[
              { required: true, message: t('planTemplate.wizard.cycleConfiguration.validation.repeatCountMustBePositive') },
              { type: 'number', min: 1, max: 1000, message: t('planTemplate.wizard.cycleConfiguration.validation.repeatCountMaxLimit') }
            ]}
          >
            <InputNumber
              min={1}
              max={1000}
              value={cycleConfig.repeatCount}
              onChange={handleRepeatCountChange}
              style={{ width: '100%' }}
              placeholder={t('planTemplate.wizard.cycleConfiguration.repeatCountPlaceholder')}
            />
          </Form.Item>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3}>{t('planTemplate.wizard.cycleConfiguration.title')}</Title>
        <Text type="secondary">
          {t('planTemplate.wizard.cycleConfiguration.description')}
        </Text>
      </div>

      {/* 第一行：周期设置和终止条件 */}
      <Row gutter={24} style={{ marginBottom: '24px' }}>
        <Col span={12}>
          <Card 
            title={t('planTemplate.wizard.cycleConfiguration.basicSettings')} 
            style={{ height: '100%' }}
            bodyStyle={{ padding: '20px' }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={() => {}}
            >
              {/* Frequency Type Selection */}
              <Form.Item
                label={t('planTemplate.wizard.cycleConfiguration.frequencyType')}
                name="frequencyType"
                rules={[{ required: true, message: t('planTemplate.wizard.cycleConfiguration.validation.selectFrequencyType') }]}
                style={{ marginBottom: '16px' }}
              >
                <Radio.Group
                  value={cycleConfig.frequencyType}
                  onChange={(e) => handleFrequencyTypeChange(e.target.value)}
                >
                  <Radio value="Daily">{t('planTemplate.wizard.cycleConfiguration.frequencyOptions.daily')}</Radio>
                  <Radio value="Weekly">{t('planTemplate.wizard.cycleConfiguration.frequencyOptions.weekly')}</Radio>
                  <Radio value="Monthly">{t('planTemplate.wizard.cycleConfiguration.frequencyOptions.monthly')}</Radio>
                  <Radio value="Yearly">{t('planTemplate.wizard.cycleConfiguration.frequencyOptions.yearly')}</Radio>
                </Radio.Group>
              </Form.Item>

              {/* Interval Configuration */}
              <Form.Item
                label={`${t('planTemplate.wizard.cycleConfiguration.interval')} (${t('planTemplate.wizard.cycleConfiguration.intervals.every')}${
                  cycleConfig.frequencyType === 'Daily' ? t('planTemplate.wizard.cycleConfiguration.intervals.days') :
                  cycleConfig.frequencyType === 'Weekly' ? t('planTemplate.wizard.cycleConfiguration.intervals.weeks') :
                  cycleConfig.frequencyType === 'Monthly' ? t('planTemplate.wizard.cycleConfiguration.intervals.months') : t('planTemplate.wizard.cycleConfiguration.intervals.years')
                })`}
                name="interval"
                rules={[
                  { required: true, message: t('planTemplate.wizard.cycleConfiguration.validation.intervalMustBePositive') },
                  { type: 'number', min: 1, max: 365, message: t('planTemplate.wizard.cycleConfiguration.validation.intervalRange') }
                ]}
                style={{ marginBottom: '16px' }}
              >
                <InputNumber
                  min={1}
                  max={365}
                  value={cycleConfig.interval}
                  onChange={handleIntervalChange}
                  style={{ width: '200px' }}
                />
              </Form.Item>

              {/* Frequency-specific options */}
              {renderFrequencySpecificOptions()}

              {/* First Execution Date */}
              <Form.Item
                label={t('planTemplate.wizard.cycleConfiguration.firstExecutionDate')}
                name="firstExecutionDate"
                rules={[{ required: true, message: t('planTemplate.wizard.cycleConfiguration.validation.selectFirstExecutionDate') }]}
                style={{ marginBottom: '0' }}
              >
                <DatePicker
                  value={cycleConfig.firstExecutionDate ? dayjs(cycleConfig.firstExecutionDate) : null}
                  onChange={handleFirstExecutionDateChange}
                  disabledDate={(current) => current && current.isBefore(dayjs(), 'day')}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col span={12}>
          <Card 
            title={t('planTemplate.wizard.cycleConfiguration.terminationSettings')} 
            style={{ height: '100%' }}
            bodyStyle={{ padding: '20px' }}
          >
            <Form form={form} layout="vertical">
              {/* Termination Type */}
              <Form.Item
                label={t('planTemplate.wizard.cycleConfiguration.terminationType')}
                name="terminationType"
                rules={[{ required: true, message: t('planTemplate.wizard.cycleConfiguration.validation.selectTerminationType') }]}
                style={{ marginBottom: '16px' }}
              >
                <Radio.Group
                  value={cycleConfig.terminationType}
                  onChange={(e) => handleTerminationTypeChange(e.target.value)}
                >
                  <Radio value="Infinite Repeat">{t('planTemplate.wizard.cycleConfiguration.terminationOptions.infiniteRepeat')}</Radio>
                  <Radio value="End on Date">{t('planTemplate.wizard.cycleConfiguration.terminationOptions.endOnDate')}</Radio>
                  <Radio value="Limited Count">{t('planTemplate.wizard.cycleConfiguration.terminationOptions.limitedCount')}</Radio>
                </Radio.Group>
              </Form.Item>

              {/* Termination-specific options */}
              <div style={{ marginBottom: '0' }}>
                {renderTerminationOptions()}
              </div>
            </Form>
          </Card>
        </Col>
      </Row>

      {/* 第二行：配置摘要 */}
      <Row style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card 
            title={t('planTemplate.wizard.cycleConfiguration.configSummary.title')} 
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ lineHeight: '1.8' }}>
              <Row gutter={32}>
                <Col span={6}>
                  <div><Text strong>{t('planTemplate.wizard.cycleConfiguration.configSummary.frequencyType')}:</Text></div>
                  <div style={{ color: '#1890ff', fontSize: '16px', fontWeight: 500 }}>
                    {cycleConfig.frequencyType === 'Daily' ? t('planTemplate.wizard.cycleConfiguration.frequencyOptions.daily') :
                     cycleConfig.frequencyType === 'Weekly' ? t('planTemplate.wizard.cycleConfiguration.frequencyOptions.weekly') :
                     cycleConfig.frequencyType === 'Monthly' ? t('planTemplate.wizard.cycleConfiguration.frequencyOptions.monthly') : t('planTemplate.wizard.cycleConfiguration.frequencyOptions.yearly')}
                  </div>
                </Col>
                
                <Col span={6}>
                  <div><Text strong>{t('planTemplate.wizard.cycleConfiguration.configSummary.interval')}:</Text></div>
                  <div style={{ color: '#1890ff', fontSize: '16px', fontWeight: 500 }}>
                    {cycleConfig.interval} {
                      cycleConfig.frequencyType === 'Daily' ? t('planTemplate.wizard.cycleConfiguration.intervals.days') :
                      cycleConfig.frequencyType === 'Weekly' ? t('planTemplate.wizard.cycleConfiguration.intervals.weeks') :
                      cycleConfig.frequencyType === 'Monthly' ? t('planTemplate.wizard.cycleConfiguration.intervals.months') : t('planTemplate.wizard.cycleConfiguration.intervals.years')
                    }
                  </div>
                </Col>

                <Col span={6}>
                  <div><Text strong>{t('planTemplate.wizard.cycleConfiguration.configSummary.firstExecution')}:</Text></div>
                  <div style={{ color: '#1890ff', fontSize: '16px', fontWeight: 500 }}>
                    {cycleConfig.firstExecutionDate ? 
                     dayjs(cycleConfig.firstExecutionDate).format('MM/DD') : 
                     t('planTemplate.wizard.cycleConfiguration.configSummary.notSet')}
                  </div>
                </Col>

                <Col span={6}>
                  <div><Text strong>{t('planTemplate.wizard.cycleConfiguration.configSummary.terminationCondition')}:</Text></div>
                  <div style={{ color: '#1890ff', fontSize: '16px', fontWeight: 500 }}>
                    {cycleConfig.terminationType === 'Infinite Repeat' ? t('planTemplate.wizard.cycleConfiguration.terminationOptions.infiniteRepeat') :
                     cycleConfig.terminationType === 'End on Date' ? 
                       `${cycleConfig.endDate ? dayjs(cycleConfig.endDate).format('MM/DD') : t('planTemplate.wizard.cycleConfiguration.configSummary.notSet')}` :
                       `${cycleConfig.repeatCount || 0} ${t('planTemplate.wizard.cycleConfiguration.configSummary.times')}`}
                  </div>
                </Col>
              </Row>

              {/* 详细配置信息 */}
              {(cycleConfig.frequencyType === 'Weekly' && cycleConfig.weekDaySelection) && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                  <Text strong>{t('planTemplate.wizard.cycleConfiguration.configSummary.executionWeekdays')}:</Text> {
                    cycleConfig.weekDaySelection
                      .map(day => WEEKDAY_OPTIONS.find(opt => opt.value === day)?.label)
                      .join(', ')
                  }
                </div>
              )}

              {(cycleConfig.frequencyType === 'Monthly' && cycleConfig.monthDaySelection) && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                  <Text strong>{t('planTemplate.wizard.cycleConfiguration.configSummary.executionDates')}:</Text> {
                    cycleConfig.monthDaySelection
                      .map(day => `${day}${t('planTemplate.wizard.cycleConfiguration.monthDays.day')}`)
                      .join(', ')
                  }
                </div>
              )}
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert
                message={t('planTemplate.wizard.cycleConfiguration.validation.configValidationFailed')}
                description={
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                }
                type="error"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* 第三行：执行时间预览 */}
      <Row>
        <Col span={24}>
          <Card 
            title={
              <Space>
                <CalendarOutlined />
                {t('planTemplate.wizard.cycleConfiguration.schedulePreview')}
              </Space>
            }
            bodyStyle={{ padding: '20px' }}
          >
            {previewDates.length > 0 ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0' }}>
                   {previewDates.slice(0, 5).map((date, index) => (
                     <div key={index} style={{ textAlign: 'center', flex: 1, position: 'relative' }}>
                       <div style={{ 
                         width: '12px', 
                         height: '12px', 
                         backgroundColor: '#1890ff', 
                         borderRadius: '50%', 
                         margin: '0 auto 8px',
                         position: 'relative',
                         zIndex: 2
                       }} />
                       <div style={{ fontSize: '16px', fontWeight: 500, color: '#1890ff' }}>
                         {dayjs(date).format('MM/DD')}
                       </div>
                       <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                         {dayjs(date).format('dddd')}
                       </div>
                       <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                         {t('planTemplate.wizard.cycleConfiguration.preview.executionNumber', { number: index + 1 })}
                       </div>
                       {index < 4 && (
                         <div style={{
                           position: 'absolute',
                           top: '6px',
                           left: '50%',
                           width: '100%',
                           height: '2px',
                           backgroundColor: '#d9d9d9',
                           zIndex: 1
                         }} />
                       )}
                     </div>
                   ))}
                 </div>
                
                {previewDates.length > 5 && (
                  <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <Text type="secondary">
                      {t('planTemplate.wizard.cycleConfiguration.preview.moreExecutions', { count: previewDates.length - 5 })}
                    </Text>
                  </div>
                )}

                {previewDates.length >= 10 && (
                  <Alert
                    message={t('planTemplate.wizard.cycleConfiguration.preview.showingFirst10')}
                    type="info"
                    showIcon
                    style={{ marginTop: '16px' }}
                  />
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <CalendarOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                <div>{t('planTemplate.wizard.cycleConfiguration.preview.completeConfigFirst')}</div>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default CycleConfiguration;